/**
 * Vocabulaire français de l'opérateur — UNE seule porte d'entrée.
 *
 * Règle absolue: l'écran n'affiche JAMAIS un code brut (CONFIRMED, DRAFT,
 * AT_CUSTOMS_EXIT, SUPPLIER_BILL…), un identifiant (UUID, trip_id…), `null`,
 * `undefined`, ni un mot anglais. Toute valeur qui vient d'une liste
 * (statut, type, catégorie, rôle…) passe par `libelle(domaine, code)`.
 *
 * Un code inconnu ne casse rien et ne fuit pas: il tombe sur « Non précisé »
 * (`libelle`) plutôt que de montrer le code. Les domaines sont complétés au
 * fil des écrans; la liste des domaines est dans `LIBELLES` ci-dessous.
 */

import { TIRET, estDateIso, formatDate, francisaTexte } from './formats';

export const NON_PRECISE = 'Non précisé';

const UUID = /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;
const HEX_LONG = /^[0-9a-f]{12,}$/i;
// Un code technique contient un underscore (AT_CUSTOMS_EXIT, trip_id), ou est un
// mot-clé technique connu (CONFIRMED, null…). Un nom (DAKAR), un sigle (PKS), une
// plaque (AB-1234-MD) ou un numéro de référence (CMD-2026-0001) n'en est JAMAIS un.
const AVEC_UNDERSCORE = /^[A-Za-z][A-Za-z0-9]*(?:_[A-Za-z0-9]+)+$/;
const MOTS_TECHNIQUES = new Set(['NULL', 'UNDEFINED', 'NAN', 'NONE', 'TRUE', 'FALSE', 'N/A']);
let clesConnues = null;
const motCleConnu = (texte) => {
  if (!/^[A-Za-z]{4,}$/.test(texte)) return MOTS_TECHNIQUES.has(texte.toUpperCase());
  if (!clesConnues) {
    clesConnues = new Set(MOTS_TECHNIQUES);
    for (const table of Object.values(LIBELLES)) {
      for (const cle of Object.keys(table)) {
        // Seules les clés écrites en MAJUSCULES sont des mots-clés techniques:
        // « Geofence » ou « city » sont des mots comme les autres.
        if (/^[A-Z]{4,}$/.test(cle)) clesConnues.add(cle);
      }
    }
  }
  // Un mot-clé technique est écrit en majuscules (CONFIRMED) ou en tout-minuscule
  // pour un mot de la table (pending); un nom propre en capitale initiale
  // (« Dakar ») n'est jamais écarté.
  return clesConnues.has(texte.toUpperCase()) && texte !== texte.charAt(0).toUpperCase() + texte.slice(1).toLowerCase();
};

/**
 * Vrai si la chaîne est un identifiant ou un code technique: UUID, hexadécimal
 * long, code à underscore, mot-clé technique connu. Jamais un nom, une plaque,
 * un sigle ni un numéro de référence.
 */
export const estCodeBrut = (valeur) => {
  if (typeof valeur !== 'string') return false;
  const v = valeur.trim();
  if (!v) return false;
  return UUID.test(v) || HEX_LONG.test(v) || AVEC_UNDERSCORE.test(v) || motCleConnu(v);
};

export const LIBELLES = {
  // Fret d'un trajet (lot 019) — mêmes codes que backend/app/core/truck_economics.py
  freight_mode: {
    FACTURE: 'Facturé à un client',
    INTERNE: 'Valeur interne de référence',
  },
  freight_basis: {
    CAPACITE: 'Capacité de la citerne',
    CHARGE: 'Quantité chargée',
    LIVRE: 'Quantité livrée',
  },
  freight_source: {
    BAREME: 'Barème de fret',
    SAISIE: 'Tarif saisi',
    MONTANT: 'Montant forfaitaire',
  },
  freight_status: {
    FACTURE: 'Facture émise',
    PREVU: 'Prévu, pas encore facturé',
    INTERNE: 'Valeur interne',
    AUCUN: 'Aucun fret posé',
  },
  freight_stage: {
    AVANCE: 'Avance',
    PAIEMENT: 'Paiement',
  },
  // Frais (lot 017) — mêmes codes que backend/app/core/expense_categories.py
  categorie_frais: {
    SALAIRE: 'Salaire',
    ASSURANCE: 'Assurance',
    LOYER: 'Fret du camion loué',
    FRAIS_ROUTE: 'Frais de route',
    FRAIS_SUPP: 'Frais supplémentaires',
    AUTRES: 'Autres frais',
    ENTRETIEN_REPARATION: 'Entretien et réparation',
    PIECES_DETACHEES: 'Pièces détachées',
    PIECES_ADMIN: 'Renouvellement des papiers',
    CARBURANT: 'Carburant du camion',
  },
  groupe_frais: {
    FIXE: 'Charges fixes',
    VARIABLE: 'Charges variables',
    MAINTENANCE: 'Maintenance',
  },
  porteur_frais: {
    SOCIETE: 'La société',
    PROPRIETAIRE: 'Le propriétaire du camion',
    CLIENT: 'Le client (à refacturer)',
  },
  type_frais_route: {
    TOLL: 'Péage',
    WEIGH_STATION: 'Pesage',
    CUSTOMS_FEE: 'Frais de douane',
    POLICE_CHECK: 'Contrôle de police',
    MEALS: 'Repas',
    REPAIRS: 'Réparation en route',
    OTHER: 'Autre frais',
  },
  // Commandes: état unique calculé par le serveur (chantier répartiteur)
  etat_commande: {
    BROUILLON: 'Brouillon',
    A_AFFECTER: 'À affecter',
    PARTIELLE: 'Affectée en partie',
    AFFECTEE: 'Affectée',
    EN_ROUTE: 'En route',
    LIVREE: 'Livrée',
    ANNULEE: 'Annulée',
  },
  // Statut enregistré d'une commande (jamais affiché seul quand l'état calculé
  // `etat_commande` existe; sert de repli et pour « Facturée » / « Payée »)
  statut_commande: {
    DRAFT: 'Brouillon',
    PENDING: 'En attente',
    CONFIRMED: 'Confirmée',
    ALLOCATED: 'Affectée',
    IN_TRANSIT: 'En route',
    PARTIALLY_DELIVERED: 'Livrée en partie',
    DELIVERED: 'Livrée',
    INVOICED: 'Facturée',
    PAID: 'Payée',
    CANCELLED: 'Annulée',
  },
  // Étape d'un trajet — mêmes codes que backend/app/core/workflow_defaults.py
  etape_trajet: {
    CREATED: 'Prévu, pas encore parti',
    EN_ROUTE_TO_BORDER: 'En route vers la frontière',
    AT_CUSTOMS_EXIT: 'À la douane de sortie',
    CLEARED_CUSTOMS_EXIT: 'Douane de sortie passée',
    EN_ROUTE_TO_LOADING: 'En route vers le chargement',
    LOADING: 'En chargement',
    EN_ROUTE_TO_ENTRY_CUSTOMS: "En route vers la douane d'entrée",
    AT_CUSTOMS_ENTRY: "À la douane d'entrée",
    CLEARED_CUSTOMS_ENTRY: "Douane d'entrée passée",
    ARRIVED_AT_DEPOT: 'Arrivé au dépôt',
    EN_ROUTE_TO_DELIVERY: 'En route vers la livraison',
    DELIVERED: 'Livré',
    COMPLETED: 'Terminé',
    CANCELLED: 'Annulé',
  },
  // Propriété d'un camion
  propriete_camion: {
    PROPRE: 'Camion de la société',
    LOUE: 'Camion loué',
  },
  // Liste des trajets (lot 056) — groupes d'étapes, mêmes codes que
  // backend/app/services/trip_list_query.py (STATUS_GROUP_LABELS).
  filtre_liste_trajets: {
    all: 'Tous les trajets',
    importation: 'Importation du carburant',
    livraison: 'Livraison aux clients',
    active: 'En cours',
    customs: 'À la douane',
    completed: 'Terminés',
  },
  // Parking d'un trajet: un état à part, pas une étape du parcours.
  parking_action: {
    ENTER: 'Mettre en parking',
    LEAVE: 'Sortir du parking',
  },
  parking_etat: {
    oui: 'En parking',
    non: 'Hors parking',
  },
  document_sortie: {
    oui: 'Document de sortie saisi',
    non: 'Sans document de sortie',
  },
  // Origine de la date de sortie douane d'un trajet.
  source_sortie_douane: {
    AUTO: 'Posée au passage de la douane d’entrée',
    MANUAL: 'Saisie à la main',
  },
  mode_remuneration_location: {
    PERIODE: 'Forfait par période',
    ROTATION: 'Prix par rotation',
    LITRE: 'Prix par litre',
    PART_FRET: 'Part du fret',
    GERE: 'Exploité pour le compte du propriétaire',
  },
  // Location entrante (lot 018) — mêmes codes que backend/app/models/sqlalchemy/inbound_rental.py
  statut_contrat_entrant: {
    BROUILLON: 'Brouillon',
    ACTIF: 'En cours',
    CLOS: 'Clos',
  },
  unite_periode: {
    JOUR: 'Jour',
    SEMAINE: 'Semaine',
    MOIS: 'Mois',
  },
  fournisseur_chauffeur: {
    PROPRIETAIRE: 'Fourni par le propriétaire',
    SOCIETE: 'Fourni par la société',
  },
  statut_reglement: {
    APERCU: 'Aperçu',
    BROUILLON: 'Brouillon',
    VALIDE: 'Validé',
  },
  // État de paiement du transport (lot 059) — codes de backend/app/models/sqlalchemy/transport_statement.py
  mode_reglement_location: {
    MENSUEL: 'Mensuel',
    ETAT: 'Par état de paiement',
  },
  statut_etat_paiement: {
    BROUILLON: 'Brouillon',
    VALIDE: 'Validé',
    ANNULE: 'Annulé',
  },
  statut_paiement_etat: {
    PAYE: 'Payé',
    PARTIEL: 'Payé en partie',
    A_PAYER: 'À payer',
  },
  // Décompte d'un lot attribué (lot 058) — codes de backend/app/models/sqlalchemy/lot_settlement.py
  statut_decompte_lot: {
    BROUILLON: 'Brouillon',
    VALIDE: 'Validé',
    ANNULE: 'Annulé',
  },
  source_charge_proprietaire: {
    work_order: 'Entretien',
    vehicle_document: 'Papier du camion',
    journal: 'Frais saisi',
  },
  nature_ecriture_reglement: {
    SUPPLIER_BILL: 'Fret à payer',
    SUPPLIER_CREDIT: 'Avoir (le propriétaire nous doit)',
    SUPPLIER_PAYMENT: 'Paiement ou avance',
  },
  sens_compte_proprietaire: {
    DU: 'À payer au propriétaire',
    REGLE: 'Réglé au propriétaire',
  },
  ligne_releve: {
    ROTATION: 'Rotation',
    CHARGE: 'Frais',
  },
  // Origine d'un élément du flux d'observations (lot 017)
  source_observation: {
    OBSERVATION: 'Observation',
    INCIDENT: 'Incident chauffeur',
    PANNE: 'Panne',
    ETAPE: 'Changement d’étape',
    FRAIS_SUPP: 'Frais supplémentaire',
  },
  type_observation: {
    INCIDENT: 'Incident',
    CONSTAT: 'Constat',
    RAPPEL: 'Rappel',
    FRAIS: 'Frais exceptionnel',
    AUTRE: 'Autre',
  },

  // ======================================================================
  // Domaines métier (chantier « vocabulaire »). Clé = code réel du backend
  // (voir frontend/src/lib/__fixtures__/enums.json). La recherche est
  // insensible à la casse: 'open' et 'OPEN' donnent le même libellé.
  // ======================================================================

  // --- Trajets ---------------------------------------------------------
  tripStatus: {
    CREATED: 'Créé',
    EN_ROUTE_TO_BORDER: 'Vers la frontière',
    AT_CUSTOMS_EXIT: 'À la douane de sortie',
    CLEARED_CUSTOMS_EXIT: 'Douane de sortie passée',
    EN_ROUTE_TO_LOADING: 'Vers le chargement',
    LOADING: 'En chargement',
    EN_ROUTE_TO_ENTRY_CUSTOMS: 'Vers la douane d’entrée',
    AT_CUSTOMS_ENTRY: 'À la douane d’entrée',
    CLEARED_CUSTOMS_ENTRY: 'Douane d’entrée passée',
    ARRIVED_AT_DEPOT: 'Arrivé au dépôt',
    EN_ROUTE_TO_DELIVERY: 'Vers la livraison',
    DELIVERED: 'Livré',
    COMPLETED: 'Terminé',
    RETURNING: 'Terminé',
    CANCELLED: 'Annulé',
    VALIDATED: 'Validé',
    LOADED: 'Chargé',
    CUSTOMS_DEPARTURE: 'Douane de départ',
    IN_TRANSIT: 'En route',
    CUSTOMS_ARRIVAL: 'Douane d’arrivée',
    UNLOADED: 'Déchargé',
    RETURNED: 'Rentré',
    PARTIALLY_DELIVERED: 'Livraison partielle',
    POD_REQUIRED: 'Preuve de livraison à fournir',
    POD_REVIEW: 'Preuve de livraison à vérifier',
    BILLING_READY: 'Prêt à facturer',
    FAILED_RECONCILIATION: 'Écart de volume à trancher',
    BLOCKED_AUDIT: 'Bloqué pour contrôle',
  },
  tripPhase: {
    IMPORTATION: 'Importation',
    LIVRAISON: 'Livraison',
    CLOTURE: 'Clôturé',
  },
  tripCategorie: {
    planned: 'Planifié',
    in_progress: 'En cours',
    blocked: 'Bloqué',
    received: 'Reçu',
    delivered: 'Livré',
    closed: 'Clôturé',
    cancelled: 'Annulé',
  },
  releasePhase: {
    BORDER_CROSSED: 'Dès la douane d’entrée franchie',
    DELIVERY_PHASE: 'Dès le passage en livraison',
    COMPLETED: 'À la clôture du trajet',
  },

  // --- Camions ---------------------------------------------------------
  truckStatus: {
    FREE: 'Disponible',
    ON_TRIP: 'En trajet',
    MAINTENANCE: 'En maintenance',
    OUT_OF_SERVICE: 'Hors service',
    RENTED_OUT: 'Loué à un tiers',
    IMMOBILIZED: 'Immobilisé',
    RETURNED_TO_OWNER: 'Rendu au propriétaire',
  },
  technicalStatus: {
    OK: 'En bon état',
    MAINTENANCE: 'En maintenance',
    BROKEN: 'En panne',
  },
  truckOwnership: {
    PROPRE: 'Camion propre',
    LOUE: 'Camion loué',
  },
  // Pays des corridors: les trajets portent un nom en clair, les corridors un code à deux lettres.
  pays: {
    ML: 'Mali',
    SN: 'Sénégal',
    CI: 'Côte d’Ivoire',
    BF: 'Burkina Faso',
    GN: 'Guinée',
    TG: 'Togo',
    GH: 'Ghana',
    NE: 'Niger',
    MR: 'Mauritanie',
    MA: 'Maroc',
    DZ: 'Algérie',
    BJ: 'Bénin',
    NG: 'Nigeria',
    GM: 'Gambie',
    FR: 'France',
  },

  // --- Commandes (le statut enregistré est `statut_commande`, l'état calculé `etat_commande`)
  orderType: {
    SALE: 'Vente client',
    INTERNAL_TRANSFER: 'Transfert interne',
  },
  orderEvent: {
    ALLOCATION: 'Affectation d’un camion',
    DELIVERY_RECORDED: 'Livraison enregistrée',
    DELIVERY_CLOSED: 'Livraison clôturée',
    STOCK_LINK: 'Lien avec le stock',
    FINANCIAL_LINK: 'Lien avec la finance',
    SOURCE_CORRECTION: 'Correction de la source',
    DECISION: 'Décision',
    EXPLANATION: 'Explication',
  },
  // Manquants: état du dossier et classement de la mesure (un seul tableau)
  shortageCase: {
    UNKNOWN: 'Mesure absente',
    IN_PROGRESS: 'Bilan en cours',
    WITHIN_TOLERANCE: 'Dans la tolérance',
    OUTSIDE_TOLERANCE: 'Hors tolérance',
    SURPLUS: 'Excédent',
    TO_REVIEW: 'À examiner',
    CONTESTED: 'Contesté',
    COMPANY_ACCEPTED: 'Accepté par l’entreprise',
    ASSIGNED: 'Responsabilité attribuée',
    SHARED: 'Responsabilité partagée',
    RECOVERY_IN_PROGRESS: 'Récupération en cours',
    RESOLVED: 'Résolu',
  },
  shortageEvent: {
    MEASUREMENT: 'Constat',
    EXPLANATION: 'Explication',
    CONTEST: 'Contestation',
    DECISION: 'Décision',
    SOURCE_CORRECTION: 'Correction de la source',
    FINANCIAL_LINK: 'Opération financière',
    STOCK_LINK: 'Lien avec le stock',
    ORPHAN_CLOSED: 'Clos : trajet introuvable',
  },
  shortageParty: {
    COMPANY: 'Entreprise',
    DRIVER: 'Chauffeur',
    CUSTOMER: 'Client',
    SUPPLIER: 'Fournisseur',
  },

  // --- Facturation et paiements -----------------------------------------
  invoiceStatus: {
    DRAFT: 'Brouillon',
    PENDING: 'À encaisser',
    SENT: 'Envoyée',
    PARTIAL: 'Paiement partiel',
    PARTIALLY_PAID: 'Paiement partiel',
    PAID: 'Payée',
    // Ancien statut, plus posé par personne: sans échéance, une facture
    // ouverte est simplement à encaisser.
    OVERDUE: 'À encaisser',
    DISPUTED: 'En litige',
    CANCELLED: 'Annulée',
  },
  paymentMethod: {
    CASH: 'Espèces',
    BANK_TRANSFER: 'Virement',
    CHECK: 'Chèque',
    MOBILE_MONEY: 'Mobile Money',
    CREDIT_CARD: 'Carte bancaire',
  },
  paymentChannel: {
    CASH: 'Espèces',
    CREDIT: 'À crédit',
    MOBILE_MONEY: 'Mobile Money',
    BANK_TRANSFER: 'Virement',
  },
  paymentTerms: {
    CASH: 'Comptant',
    CREDIT_7: 'Crédit 7 jours',
    CREDIT_15: 'Crédit 15 jours',
    CREDIT_30: 'Crédit 30 jours',
    CREDIT_60: 'Crédit 60 jours',
  },

  // --- Clients et convois -------------------------------------------------
  clientStatus: {
    ACTIVE: 'Actif',
    SUSPENDED: 'Suspendu',
    INACTIVE: 'Inactif',
  },
  clientType: {
    DIRECT: 'Direct',
    THIRD_PARTY: 'Donneur d’ordre tiers',
    GOVERNMENT: 'Administration',
    INTERNAL: 'Interne',
  },
  operationType: {
    INTERNAL: 'Interne',
    RENTAL: 'Location',
    CLIENT: 'Client',
  },

  // --- Stock ---------------------------------------------------------------
  stockMovement: {
    IMPORT: 'Entrée',
    SALE: 'Vente',
    INTERNAL_TRANSFER: 'Transfert interne',
    INTERNAL_CONSUMPTION: 'Consommation interne',
    ADJUSTMENT: 'Ajustement',
    LOSS: 'Perte',
    LOADING: 'Chargement',
    DELIVERY: 'Livraison',
    RETURN: 'Retour',
    IN_TRANSIT: 'En transit',
  },
  stockLocation: {
    IN_TRANSIT: 'En transit',
    DEPOT: 'Dépôt',
    STATION: 'Station',
    STATION_TANK: 'Cuve de station',
    CUSTOMER: 'Client',
    CLIENT: 'Client',
    INTERNAL_TANK: 'Cuve interne',
    ASSET: 'Engin',
    PROJECT: 'Projet',
    DEPARTMENT: 'Département',
  },
  // Origine d'une ligne du journal de stock: saisie à la main ou écrite par le système.
  stockSource: {
    MANUAL: 'Saisie à la main',
    AUTO_TRIP_ARRIVAL: 'Arrivée au dépôt',
    AUTO_TRIP_COMPLETION: 'Fin de trajet',
    AUTO_DISPATCH: 'Répartition',
    CONTROL_FUEL: 'Contrôle carburant',
    STATION_POS: 'Caisse de station',
    STATION_SHIFT: 'Ventes d’un poste de station',
    STATION_SHIFT_REOPEN: 'Réouverture d’un poste de station',
    AUTO_TRIP_LOADING: 'Chargement d’un trajet',
    AUTO_TRIP_BORDER_CROSSING: 'Passage de la douane d’entrée',
    AUTO_TRIP_DIRECT_DELIVERY: 'Livraison directe',
    AUTO_TRIP_DIRECT_IMPORT: 'Importation directe',
    STATION_TANK_OPENING: 'Stock initial de cuve',
    STATION_RECEPTION: 'Réception en station',
    STATION_TANK_ADJUSTMENT: 'Ajustement de cuve',
  },

  // Familles de la vue d'ensemble (clé `section.key` du pilotage). Le serveur
  // envoie un titre interne (« Cockpit Transport Distribution »): l'écran
  // affiche celui-ci.
  sectionPilotage: {
    transport: 'Transport et livraisons',
    stations: 'Stations du réseau',
    control_fuel: 'Contrôle du carburant',
    group: 'Carburant du groupe',
  },

  // --- Utilisateurs, sociétés, licences -------------------------------------
  userRole: {
    SUPER_ADMIN: 'Super admin',
    ADMIN_SOCIETE: 'Administrateur',
    ADMIN: 'Administrateur',
    LOGISTIQUE: 'Logistique',
    MAINTENANCE: 'Maintenance',
    GESTIONNAIRE_STATION: 'Gestionnaire station',
    GERANT_STATION: 'Gérant station',
    AGENT_STATION: 'Agent station',
    CONTROLEUR_CARBURANT: 'Contrôleur carburant',
    CHAUFFEUR: 'Chauffeur',
    CLIENT_PORTAL: 'Portail client',
    PERSONNEL: 'Personnel',
    // Anciens rôles encore présents dans des comptes ou des journaux.
    TRANSIT: 'Logistique',
    FINANCE_STATION: 'Gestionnaire station',
  },
  orgType: {
    RENTAL_TRUCKS: 'Loue des camions',
    OWN_TRUCKS: 'Camions propres',
    OWN_AND_RENTED_TRUCKS: 'Camions propres + loués',
    OWN_TRUCKS_AND_STATIONS: 'Camions propres + stations',
    OTHER: 'Autre activité',
  },
  licensePlan: {
    TRANSPORT_DISTRIBUTION: 'Transport',
    STATIONS_RESEAU: 'Réseau stations',
    CONTROLE_CARBURANT: 'Contrôle carburant',
    ENTERPRISE: 'Entreprise',
  },
  licenseStatus: {
    ACTIVE: 'Active',
    SUSPENDED: 'Suspendue',
    EXPIRED: 'Expirée',
    REVOKED: 'Révoquée',
  },
  licenseAudit: {
    CREATED: 'Créée',
    RENEWED: 'Renouvelée',
    SUSPENDED: 'Suspendue',
    REACTIVATED: 'Réactivée',
    PLAN_CHANGED: 'Formule modifiée',
    MODULES_CHANGED: 'Modules modifiés',
    LIMITS_CHANGED: 'Limites modifiées',
    PRICE_CHANGED: 'Prix modifié',
    REVOKED: 'Révoquée',
  },
  auditAction: {
    CREATE: 'Création',
    UPDATE: 'Modification',
    DELETE: 'Suppression',
    USER_CREATED: 'Utilisateur créé',
    USER_UPDATED: 'Utilisateur modifié',
    USER_DELETED: 'Utilisateur supprimé',
    USER_SUSPENDED: 'Utilisateur suspendu',
    USER_REACTIVATED: 'Utilisateur réactivé',
    ORGANIZATION_SUSPENDED: 'Société suspendue',
    ORGANIZATION_REACTIVATED: 'Société réactivée',
    ORG_SUSPENDED: 'Société suspendue',
    LOGIN_ACCESS_CODE: 'Connexion par code d’accès',
    LOGIN_SUCCESS: 'Connexion réussie',
    LOGIN_FAILED: 'Connexion refusée',
    AUTH_FAILED: 'Connexion refusée',
    ACCESS_CODE_REGENERATED: 'Code d’accès renouvelé',
    LICENSE_CREATED: 'Licence créée',
    LICENSE_REVOKED: 'Licence révoquée',
    LICENSE_SUSPENDED: 'Licence suspendue',
    BACKEND_OP_PURGE: 'Purge technique',
    BACKEND_OP_RESTORE: 'Restauration technique',
    BACKEND_OP_FLUSH: 'Vidage technique',
    BACKEND_OP_MIGRATION: 'Mise à jour technique',
    INVOICE_CORRECTED: 'Facture corrigée',
    INVOICE_CANCELLED: 'Facture annulée',
    INVOICE_CREATED_WITH_ADVISORIES: 'Facture créée avec des points à contrôler',
    TRANSPORT_INVOICE_CREATED: 'Facture de transport créée',
    FINANCIAL_HISTORY_ADOPTED: 'Historique financier repris',
    FINANCIAL_TRIP_HISTORY_ADOPTED: 'Historique financier du trajet repris',
    FINANCIAL_HISTORY_RECONCILED: 'Historique financier rapproché',
    FINANCIAL_HISTORY_DIVERGENCE: 'Écart dans l’historique financier',
    TRANSITION: 'Changement d’étape',
    ISSUE: 'Sortie de carburant',
    DISPATCH: 'Répartition',
    DELIVERY: 'Livraison',
    CLOSE: 'Clôture',
    'whatsapp_bot.denied': 'Accès WhatsApp refusé',
    'whatsapp_bot.unlinked': 'Accès WhatsApp délié',
    'whatsapp_bot.revoked': 'Accès WhatsApp retiré',
    'whatsapp_bot.link_code_issued': 'Code de liaison WhatsApp émis',
    'whatsapp_bot.granted': 'Accès WhatsApp accordé',
    'whatsapp_bot.demo_toggle': 'Démonstration WhatsApp modifiée',
    'whatsapp_bot.brief_settings': 'Réglage du point WhatsApp modifié',
    'whatsapp_bot.brief_sent': 'Point WhatsApp envoyé',
    'fleet_reports.month_closed': 'Mois clôturé',
    'station.created': 'Station créée',
    'station.updated': 'Station modifiée',
    'station.archived': 'Station archivée',
    'station.settings_updated': 'Seuils de la station modifiés',
    'station.product_created': 'Produit créé',
    'station.product_updated': 'Produit modifié',
    'station.tank_created': 'Cuve créée',
    'station.tank_updated': 'Cuve modifiée',
    'station.tank_chart_set': 'Barème de cuve enregistré',
    'station.pump_created': 'Pompe créée',
    'station.pump_updated': 'Pompe modifiée',
    'station.nozzle_created': 'Pistolet créé',
    'station.nozzle_updated': 'Pistolet modifié',
    'station.attendant_created': 'Pompiste ajouté',
    'station.attendant_updated': 'Pompiste modifié',
    'station.price_published': 'Prix publié',
    'station.shift_opened': 'Poste ouvert',
    'station.price_readings_recorded': 'Index relevés au changement de prix',
    'station.sale_recorded': 'Vente détaillée enregistrée',
    'station.sale_cancelled': 'Vente détaillée annulée',
    'station.expense_recorded': 'Dépense de station enregistrée',
    'station.shift_closed': 'Poste clôturé',
    'station.shift_reopened': 'Poste rouvert',
    'station.day_locked': 'Journée de station arrêtée',
    'station.day_unlocked': 'Journée de station rouverte',
    'station.day_validated': 'Journée validée par le siège',
    'station.ceiling_created': 'Plafond de prix enregistré',
    'station.dip_recorded': 'Jaugeage enregistré',
    'station.reception_planned': 'Réception préparée',
    'station.reception_started': 'Dépotage commencé',
    'station.reception_validated': 'Réception validée',
    'station.reception_cancelled': 'Réception annulée',
    'station.tank_adjusted': 'Stock de cuve ajusté',
    'station.credit_account_set': 'Compte crédit réglé',
    'station.credit_blocked': 'Compte crédit bloqué',
    'station.credit_unblocked': 'Compte crédit débloqué',
    'station.fleet_vehicle_added': 'Véhicule ajouté à une flotte',
    'station.fleet_vehicle_updated': 'Véhicule de flotte modifié',
    'station.vouchers_issued': 'Bons carburant émis',
    'station.voucher_cancelled': 'Bon carburant annulé',
    'station.voucher_used': 'Bon carburant utilisé',
    'station.voucher_released': 'Bon carburant rendu',
    'station.payment_method_updated': 'Moyen de paiement modifié',
    'station.statement_invoiced': 'Relevé mensuel facturé',
    'station.payment_import_created': 'Relevé de paiement importé',
    'station.payment_import_matched': 'Relevé de paiement rapproché',
    'station.price_change_scheduled': 'Changement de prix programmé',
    'station.price_change_cancelled': 'Changement de prix annulé',
    'station.price_change_applied': 'Changement de prix appliqué',
    'station.price_change_refused': 'Changement de prix refusé',
    'station.price_change_failed': 'Changement de prix en échec technique',
    'station.management_updated': 'Conditions de gérance modifiées',
    'station.manager_statement_frozen': 'Relevé du gérant figé',
    'station.manager_statement_voided': 'Relevé du gérant annulé',
    'station.pump_event_created': 'Événement de pompe ajouté',
    'station.pump_event_updated': 'Événement de pompe complété',
    'station.network_imported': 'Réseau repris depuis Excel',
    'station.annex_sale': 'Vente annexe',
    'station.annex_empties_returned': 'Vides rendus',
    'station.annex_received': 'Entrée de marchandise',
    'station.annex_adjusted': 'Correction de stock',
    'station.annex_item_created': 'Article créé',
    'station.annex_terms_updated': 'Conditions d’article modifiées',
  },
  auditEntity: {
    client: 'Client',
    fuel_issue: 'Sortie de cuve',
    geo_location_event: 'Passage de zone',
    invoice: 'Facture',
    operation: 'Convoi',
    org_blocking_policy: 'Règles de blocage',
    pricing_rule: 'Règle tarifaire',
    rental_contract: 'Contrat de location',
    trip: 'Trajet',
    truck: 'Camion',
    workflow_instance: 'Parcours du trajet',
    order: 'Commande',
    alert_event: 'Alerte',
    backend_op: 'Opération technique',
    finance_operation: 'Opération financière',
    organization: 'Société',
    user: 'Utilisateur',
    whatsapp_bot_access: 'Accès WhatsApp',
    driver: 'Chauffeur',
    company: 'Société partenaire',
    document: 'Document',
    license: 'Licence',
    observation: 'Observation',
    station: 'Station',
    station_product: 'Produit de station',
    station_tank: 'Cuve de station',
    station_pump: 'Pompe',
    station_nozzle: 'Pistolet',
    station_attendant: 'Pompiste',
    station_price: 'Prix de la station',
    station_price_ceiling: 'Plafond de prix',
    station_shift: 'Poste de station',
    station_sale: 'Vente de station',
    station_expense: 'Dépense de station',
    station_day: 'Journée de station',
    station_dip: 'Jaugeage',
    station_reception: 'Réception de camion',
    station_fleet_vehicle: 'Véhicule de flotte',
    station_voucher: 'Bon carburant',
    station_payment_method: 'Moyen de paiement',
    station_payment_import: 'Relevé de paiement',
    station_price_change: 'Changement de prix programmé',
    station_pump_event: 'Événement de pompe',
    station_manager_statement: 'Relevé du gérant',
    station_item_movement: 'Mouvement d’article',
  },

  // Tables de données vues par l'administrateur de la plateforme (opérations techniques).
  tableDonnees: {
    alert_decisions: 'Décisions sur alertes',
    alert_events: 'Alertes',
    alert_rules: 'Règles d’alerte',
    analytics_events: 'Statistiques d’usage',
    arbitration_certificates: 'Certificats d’arbitrage',
    assets: 'Engins',
    audit_logs: 'Journal des actions',
    client_access_policies: 'Règles d’accès des clients',
    clients: 'Clients',
    companies: 'Sociétés partenaires',
    compliance_documents: 'Documents de conformité',
    contracts: 'Contrats',
    corridor_waypoints: 'Étapes des corridors',
    corridors: 'Corridors',
    daily_reports: 'Rapports journaliers',
    delivery_proof_packs: 'Preuves de livraison',
    demo_snapshots: 'Données de démonstration',
    departments: 'Départements',
    documents: 'Documents',
    domain_events: 'Événements métier',
    driver_border_events: 'Passages de frontière des chauffeurs',
    driver_departure_checklists: 'Contrôles avant départ',
    driver_field_settings: 'Réglages terrain des chauffeurs',
    driver_gauge_readings: 'Relevés de jauge des chauffeurs',
    driver_incidents: 'Incidents signalés par les chauffeurs',
    drivers: 'Chauffeurs',
    ecarbu_score_snapshots: 'Historique des scores',
    evidence_captures: 'Preuves enregistrées',
    export_jobs: 'Exports demandés',
    finance_parties: 'Tiers financiers',
    financial_allocations: 'Affectations financières',
    financial_operations: 'Opérations financières',
    fuel_issues: 'Sorties de carburant',
    fuel_quotas: 'Plafonds de carburant',
    fuel_requests: 'Demandes de carburant',
    geo_location_events: 'Passages de zone',
    geo_locations: 'Lieux',
    geo_reference_points: 'Points de repère',
    gps_history: 'Historique des positions',
    gps_providers: 'Fournisseurs de suivi',
    idempotency: 'Contrôle des doublons',
    inbound_rental_contracts: 'Contrats de location reçus',
    inbound_rental_settlements: 'Règlements de location reçus',
    inbound_rental_trucks: 'Camions loués à des tiers',
    invoices: 'Factures',
    license_audit: 'Historique des licences',
    licenses: 'Licences',
    maintenance_breakdowns: 'Pannes',
    maintenance_immobilizations: 'Immobilisations',
    maintenance_odometer_readings: 'Relevés de compteur',
    maintenance_plans: 'Plans d’entretien',
    maintenance_vehicle_documents: 'Papiers des véhicules',
    maintenance_work_order_parts: 'Pièces des ordres de travail',
    maintenance_work_orders: 'Ordres de travail',
    mission_anomalies: 'Anomalies de livraison',
    mission_integrity_scores: 'Contrôles de livraison',
    notification_acknowledgements: 'Accusés de lecture',
    numbering_sequences: 'Numérotation',
    observations: 'Observations',
    openwa_outbox: 'Messages WhatsApp à envoyer',
    openwa_webhook_inbox: 'Messages WhatsApp reçus',
    operational_ledger: 'Journal des opérations',
    operations: 'Convois',
    orders: 'Commandes',
    org_blocking_policies: 'Règles de blocage',
    org_volume_settings: 'Réglages de volume',
    organizations: 'Organisations',
    pricing_rules: 'Règles tarifaires',
    projects: 'Projets',
    rental_charges: 'Charges de location',
    rental_contracts: 'Contrats de location',
    rental_inspections: 'États des lieux',
    rental_periods: 'Périodes de location',
    rental_rotations: 'Rotations de location',
    report_logs: 'Rapports envoyés',
    report_recipients: 'Destinataires des rapports',
    report_templates: 'Modèles de rapport',
    sites: 'Sites',
    station_attendants: 'Pompistes',
    station_dips: 'Jaugeages de cuve',
    station_expenses: 'Dépenses de station',
    station_fleet_vehicles: 'Véhicules de flotte',
    station_item_movements: 'Mouvements d’articles',
    station_item_terms: 'Conditions des articles',
    station_nozzles: 'Pistolets',
    station_payment_imports: 'Relevés de paiement',
    station_payment_methods: 'Moyens de paiement',
    station_price_ceilings: 'Plafonds de prix',
    station_price_changes: 'Changements de prix programmés',
    station_pump_events: 'Registre des pompes',
    station_manager_statements: 'Relevés du gérant',
    station_price_history: 'Historique des prix',
    station_products: 'Produits de station',
    station_pumps: 'Pompes',
    station_purchases: 'Achats de station',
    station_receptions: 'Réceptions de camion',
    station_sales: 'Ventes de station',
    station_shifts: 'Postes de station',
    station_statement_sales: 'Ventes facturées au relevé',
    station_stations: 'Stations',
    station_tanks: 'Cuves',
    station_days: 'Journées de station',
    station_shift_readings: 'Relevés d’index',
    station_vouchers: 'Bons carburant',
    stock_movements: 'Mouvements de stock',
    treasury_accounts: 'Comptes de trésorerie',
    treasury_balance_checks: 'Contrôles de solde',
    trip_allocations: 'Répartitions des commandes',
    trip_updates: 'Mises à jour de trajet',
    trips: 'Trajets',
    truck_assets: 'Équipements des camions',
    truck_charge_rules: 'Règles de charges des camions',
    trucks: 'Camions',
    users: 'Utilisateurs',
    variance_case_events: 'Suivi des écarts',
    volume_reconciliation_lines: 'Lignes de comparaison des volumes',
    volume_reconciliations: 'Comparaisons des volumes',
    volume_tolerance_rules: 'Tolérances de volume',
    volume_variance_approvals: 'Validations d’écarts',
    whatsapp_bot_access: 'Accès WhatsApp',
    whatsapp_bot_private_prices: 'Prix privés WhatsApp',
    workflow_history: 'Historique des parcours',
    workflow_instances: 'Parcours en cours',
    workflow_steps: 'Étapes de parcours',
    workflow_templates: 'Modèles de parcours',
    workflow_transitions: 'Passages d’étape',
  },

  // --- Stations -----------------------------------------------------------------
  stationStatus: {
    ACTIVE: 'Active',
    MAINTENANCE: 'En maintenance',
    INACTIVE: 'Inactive',
  },
  fuelProduct: {
    DIESEL: 'Gasoil',
    GASOLINE: 'Essence',
    GASOIL: 'Gasoil',
    SUPER: 'Super',
    PETROLE: 'Pétrole lampant',
  },
  shiftStatus: {
    OPEN: 'Ouvert',
    CLOSED: 'Clôturé',
  },

  // --- Accès des clients au portail (lot 053) — codes de
  // backend/app/services/external_access_policy_service.py (`policy_scope`, `policy_state`)
  portail_etendue: {
    client: 'Tous ses voyages',
    camion: 'Un camion',
    voyage: 'Un voyage',
    commande: 'Une commande',
    contrat: 'Un contrat',
  },
  portail_etat: {
    actif: 'Actif',
    expire: 'Terminé',
    inactif: 'Coupé',
  },

  // --- Lots attribués (lot 057) — mêmes codes que backend/app/core/lot_progress.py
  // et backend/app/models/sqlalchemy/attributed_lot.py
  lot_statut: {
    OUVERT: 'Ouvert',
    CLOS: 'Clos',
  },
  lot_ligne_etat: {
    ATTRIBUE: 'Attribué',
    CONFIRME: 'Confirmé',
  },
  lot_etape: {
    ATTRIBUE: 'Attribué',
    CHARGEMENT: 'Chargement',
    EN_ROUTE: 'En route',
    DOUANE_MALI: 'Douane Mali',
    PARKING: 'Parking',
    SORTI_DOUANE: 'Sorti de douane',
    EN_LIVRAISON: 'En livraison',
    DEPOTE: 'Dépoté',
    ANNULE: 'Annulé',
  },
  lot_retour: {
    A_RENDRE: 'À rendre',
    EN_RETOUR: 'En retour',
    RENDU: 'Rendu',
  },

  // --- Programme Stations (lots 029 à 037) — mêmes codes que backend/app/core/station_codes.py
  station_mode: {
    OWNED: 'Exploitée par nous',
    MANAGED: 'Confiée à un gérant',
    DEALER: 'Revendeur',
  },
  station_famille: {
    FUEL: 'Carburant',
    GAS: 'Gaz',
    LUBRICANT: 'Lubrifiant',
    SHOP: 'Boutique',
    SERVICE: 'Prestation',
  },
  station_unite: {
    L: 'litre',
    KG: 'kg',
    PIECE: 'pièce',
    SERVICE: 'prestation',
  },
  station_poste: {
    OPEN: 'Ouvert',
    CLOSED: 'Clôturé',
  },
  station_journee: {
    OPEN: 'Journée ouverte',
    LOCKED: 'Journée arrêtée',
    VALIDATED: 'Validée par le siège',
  },
  // Vente détaillée (lot 030) : valide, ou annulée avec un motif pendant le poste.
  station_vente: {
    VALID: 'Valide',
    CANCELLED: 'Annulée',
  },
  station_releve: {
    OPENING: 'Ouverture',
    PRICE_CHANGE: 'Changement de prix',
    CLOSING: 'Clôture',
  },
  station_source: {
    MANUAL: 'Saisi',
    PHOTO: 'Photo',
    CONTROLLER: 'Boîtier',
    GAUGE: 'Jauge',
  },
  station_paiement: {
    CASH: 'Espèces',
    ORANGE_MONEY: 'Orange Money',
    MOOV_MONEY: 'Moov Money',
    WAVE: 'Wave',
    CARD: 'Carte',
    CREDIT: 'Crédit client',
    VOUCHER: 'Bon carburant',
    FLEET: 'Flotte',
    OWN_TRUCK: 'Camion de la société',
  },
  station_activite: {
    shift_opened: 'Poste ouvert',
    shift_closed: 'Poste clôturé',
    sale: 'Vente',
    expense: 'Dépense',
    reception: 'Réception validée',
    dip: 'Jaugeage',
    price_change: 'Prix modifié',
    cash_variance: 'Écart de caisse',
    tank_variance: 'Écart de cuve',
  },
  station_jaugeage: {
    DAY_OPEN: 'Ouverture de journée',
    DAY_CLOSE: 'Fermeture de journée',
    RECEPTION_BEFORE: 'Avant dépotage',
    RECEPTION_AFTER: 'Après dépotage',
    CONTROL: 'Contrôle',
  },
  station_reception: {
    EXPECTED: 'Attendue',
    IN_PROGRESS: 'En cours',
    VALIDATED: 'Validée',
    CANCELLED: 'Annulée',
  },
  commande_nature: {
    SALE: 'Vente',
    INTERNAL_SUPPLY: 'Réapprovisionnement de ma station',
  },
  // Crédit client, bons et relevés de paiement (lot 033).
  station_bon: {
    ISSUED: 'Émis',
    USED: 'Utilisé',
    CANCELLED: 'Annulé',
    EXPIRED: 'Périmé',
  },
  station_rapprochement: {
    MATCHED: 'Rapprochée',
    UNMATCHED: 'Sans vente',
    DUPLICATE: 'Déjà importée',
  },
  station_rapprochement_par: {
    REFERENCE: 'Par référence',
    AMOUNT_TIME: 'Par montant et heure',
    AMOUNT_DAY: 'Par montant et jour',
  },
  // Réseau, gérance et conformité (lot 034).
  station_prix_programme: {
    SCHEDULED: 'Programmé',
    APPLIED: 'Appliqué',
    CANCELLED: 'Annulé',
    FAILED: 'Échec technique',
  },
  station_prix_resultat: {
    APPLIED: 'Appliqué',
    REFUSED: 'Refusé',
    ERROR: 'Échec technique',
  },
  station_pompe_evenement: {
    CALIBRATION: 'Étalonnage',
    SEAL: 'Scellés',
    INSPECTION: 'Inspection',
    BREAKDOWN: 'Panne',
  },
  station_echeance: {
    OVERDUE: 'En retard',
    SOON: 'Bientôt',
    OK: 'À jour',
    NONE: 'Sans échéance',
  },
  station_releve_gerant: {
    FROZEN: 'Figé',
    VOID: 'Annulé',
  },
  station_reprise: {
    created: 'Créé',
    skipped: 'Existe déjà',
    error: 'Refusé',
  },
  // Import de camions et de chauffeurs depuis Excel : le travail, puis chaque ligne du brouillon.
  import_flotte_travail: {
    queued: 'Analyse en cours',
    analyzing: 'Analyse en cours',
    draft: 'Brouillon à relire',
    confirmed: 'Import en cours',
    importing: 'Import en cours',
    done: 'Terminé',
    failed: 'Échec',
    cancelled: 'Annulé',
  },
  import_flotte_ligne: {
    ready: 'Prête',
    review: 'À vérifier',
    refused: 'Refusée',
    // Liste des camions d'un lot (lot 057) : la plaque est déjà dans le lot, la ligne est laissée de côté.
    in_lot: 'Déjà dans le lot',
  },
  // Comment les lignes d'une liste de camions de lot ont été lues (lot 057).
  import_lot_source: {
    ai: 'Colonnes préparées par l’IA',
    names: 'Colonnes reconnues par leur nom',
    pdf_ai: 'PDF lu par l’IA',
    pdf_text: 'Texte du PDF',
    pdf_ocr: 'PDF lu par OCR',
  },
  // Signaux de dérive d'une station (lot 037).
  station_signal: {
    NOZZLE_DRIFT: 'Dérive d’un pistolet',
    ATTENDANT_CASH: 'Écarts de caisse répétés',
    INDEX_REENTERED: 'Index ressaisis à l’ouverture',
    RECALIBRATION_READY: 'Barème à recalibrer',
  },
  // Verdict d'un recalibrage de barème (lot 037).
  station_recalibrage: {
    INSUFFICIENT: 'Pas assez de jours mesurés',
    NO_CHART: 'Cuve sans barème',
    NOT_NEEDED: 'Barème cohérent',
    OUT_OF_RANGE: 'Écart trop grand, à vérifier sur place',
    READY: 'Correction proposée',
    APPLIED: 'Barème corrigé',
  },
  // Lecture d'une photo de compteur (lot 037).
  station_lecture_photo: {
    READ: 'Index lu',
    AMBIGUOUS: 'Plusieurs index possibles',
    IMPLAUSIBLE: 'Index illogique',
    UNREADABLE: 'Photo illisible',
    UNAVAILABLE: 'Lecture indisponible',
  },
  // Fuseau d'une station : la journée de station suit l'heure locale.
  station_fuseau: {
    'Africa/Bamako': 'Heure du Mali',
    'Africa/Abidjan': 'Heure de Côte d’Ivoire',
    'Africa/Dakar': 'Heure du Sénégal',
    'Africa/Ouagadougou': 'Heure du Burkina Faso',
    'Africa/Niamey': 'Heure du Niger',
    'Africa/Conakry': 'Heure de Guinée',
    'Africa/Nouakchott': 'Heure de Mauritanie',
    'Africa/Lome': 'Heure du Togo',
    'Africa/Porto-Novo': 'Heure du Bénin',
  },
  // Activités annexes d'une station (lot 036) : types de mouvement du registre des articles.
  station_article_mouvement: {
    IN: 'Entrée',
    SALE: 'Vente',
    ADJUST: 'Correction',
    RETURN_EMPTY: 'Vides rendus',
  },

  // --- Exports, parcours, intégrations -------------------------------------------
  exportJob: {
    PENDING: 'En attente',
    RUNNING: 'En cours',
    COMPLETED: 'Terminé',
    FAILED: 'Échec',
  },
  workflowModule: {
    trip: 'Trajet',
    order: 'Commande',
    invoice: 'Facture',
    stock: 'Stock',
    station_shift: 'Poste de station',
    fuel_request: 'Demande de carburant',
  },
  workflowInstance: {
    active: 'En cours',
    completed: 'Terminé',
    cancelled: 'Annulé',
  },
  // Alertes WhatsApp que chacun choisit dans « Mon profil »: les six familles
  // (mêmes codes que `ALERT_CATEGORIES` côté serveur), leur aide d'une ligne,
  // puis les textes des heures calmes.
  categorieAlerteWhatsapp: {
    trajets: 'Trajets et frontières',
    manquants: 'Manquants',
    documents: 'Documents des chauffeurs',
    echeances: 'Échéances des papiers et entretiens',
    escalades: 'Alertes en retard',
    brief: 'Brief du matin',
  },
  categorieAlerteWhatsappAide: {
    trajets: 'Entrées et sorties de zone, étapes du trajet, douane, camion sans signal.',
    manquants: 'Litres manquants constatés à la livraison.',
    documents: 'Copie des photos et documents envoyés par les chauffeurs.',
    echeances: 'Un seul récapitulatif par jour : assurances, visites, entretiens, retours de location.',
    escalades: 'Alertes graves restées sans réponse.',
    brief: 'Le résumé du matin envoyé par le bot WhatsApp.',
  },
  choixAlertesWhatsapp: {
    titre: 'Alertes à recevoir',
    calme_titre: 'Ne pas me déranger de',
    calme_debut: 'Début des heures calmes',
    calme_fin: 'Fin des heures calmes',
    calme_a: 'à',
    calme_aide: 'Les alertes critiques passent toujours. Laissez les deux heures vides pour être prévenu à toute heure.',
    calme_incomplet: 'Renseignez l’heure de début et l’heure de fin des heures calmes, ou videz les deux.',
    calme_identique: 'Les heures calmes doivent commencer et finir à des heures différentes.',
  },
  workflowSource: {
    web: 'Bureau',
    driver_app: 'Application chauffeur',
    sync: 'Mise à jour automatique',
    system: 'Automatique',
  },
  whatsappStatut: {
    pending: 'En attente',
    sent: 'Envoyée',
    accepted: 'Acceptée',
    processing: 'En cours d’envoi',
    delivered: 'Remise',
    read: 'Lue',
    failed: 'Échec',
    dead: 'Abandonnée',
  },
  // Surveillance automatique du canal WhatsApp (écran « Alertes WhatsApp »):
  // les intitulés des lignes, puis l'état de chacune en mots simples.
  surveillanceWhatsapp: {
    titre: 'Surveillance automatique',
    ligne_session: 'Session WhatsApp',
    ligne_reception: 'Réception des messages',
    ligne_accuse: 'Dernier accusé de réception',
    ligne_recu: 'Dernier message reçu',
    ligne_attente: 'Plus ancien message en attente',
    ligne_temoin: 'Message témoin du matin',
    verification: 'Dernière vérification',
    verification_jamais: 'pas encore faite',
    session_connectee: 'Connectée',
    session_coupee: 'Coupée',
    session_inconnue: 'Non vérifiée',
    relance: 'relance automatique',
    relance_epuisee: 'Les relances automatiques n’ont pas suffi : relancez la session à la main, ou reliez le numéro si un code QR est demandé.',
    adresse_ok: 'Adresse correcte',
    adresse_corrigee: 'Adresse corrigée automatiquement',
    adresse_incorrecte: 'Adresse incorrecte : à vérifier',
    adresse_inconnue: 'Non vérifiée',
    adresse_attendue: 'Adresse attendue',
    adresse_enregistree: 'Adresse enregistrée',
    adresse_aucune: 'aucune',
    aucun: 'Aucun',
    attente_vide: 'La file d’envoi est vide',
    temoin_desactive: 'Désactivé',
    temoin_recu: 'Reçu',
    temoin_manque: 'Non reçu ce matin',
    temoin_attendu: 'Pas encore envoyé',
    temoin_envoye: 'Dernier envoi',
  },
  portail: {
    MISSION: 'Mission',
    BILLING_READY: 'Prêt à facturer',
  },
  syncAction: {
    create: 'Création',
    update: 'Mise à jour',
    delete: 'Suppression',
    upsert: 'Mise à jour',
    allocate: 'Affectation',
    auto_allocate: 'Affectation automatique',
    delivery: 'Livraison',
    close: 'Clôture',
    status_update: 'Changement d’état',
  },
  syncEntity: {
    truck: 'Camion',
    driver: 'Chauffeur',
    company: 'Société',
    operation: 'Convoi',
    trip: 'Trajet',
    trip_update: 'Suivi de trajet',
    client: 'Client',
    order: 'Commande',
    invoice: 'Facture',
    stock_movement: 'Mouvement de stock',
    station: 'Station',
    station_tank: 'Cuve',
    station_pump: 'Pompe',
    station_attendant: 'Pompiste',
    station_price: 'Prix de la station',
    station_purchase: 'Achat de la station',
    station_sale: 'Vente de la station',
    station_shift: 'Poste de station',
    station_expense: 'Dépense de la station',
    expense: 'Dépense',
  },
  syncStatut: {
    pending: 'En attente',
    synced: 'Envoyé',
    failed: 'Échec',
  },

  // --- Documents --------------------------------------------------------------------
  documentType: {
    INVOICE: 'Facture',
    RECEIPT: 'Reçu carburant',
    CUSTOMS_DECLARATION: 'Déclaration douanière',
    WAYBILL: 'Lettre de voiture',
    DELIVERY_NOTE: 'Bon de livraison',
    POD_SIGNATURE: 'Signature du destinataire',
    VEHICLE_REGISTRATION: 'Carte grise',
    INSURANCE: 'Assurance',
    TECHNICAL_INSPECTION: 'Visite technique',
    IDENTITY_DOCUMENT: "Pièce d'identité",
    OTHER: 'Autre',
  },
  docVehicule: {
    insurance: 'Assurance',
    technical_inspection: 'Visite technique',
    registration: 'Carte grise',
    transport_licence: 'Licence de transport',
    tax_sticker: 'Vignette',
    business_licence: 'Patente',
    transit_pass: 'Laissez-passer',
    tank_calibration: 'Barémage de la citerne',
    hazmat_authorization: 'Autorisation matières dangereuses',
    fire_extinguisher: 'Extincteur',
    other: 'Autre',
  },
  docComponent: {
    tractor: 'Tracteur',
    tank: 'Citerne',
    whole: 'Ensemble',
  },
  docExpiry: {
    valid: 'Valide',
    expiring: 'Bientôt expiré',
    expired: 'Expiré',
    unknown: 'Date inconnue',
  },
  pieceExigee: {
    WAYBILL: 'Lettre de voiture',
    CUSTOMS_DECLARATION: 'Déclaration douanière',
    T1_TRANSIT: 'Titre de transit (T1)',
    INSURANCE: 'Assurance / carte brune',
    ADR_CERT: 'Certificat ADR',
    TRUCK_PAPERS: 'Carte grise et visite technique',
    DRIVER_LICENSE: 'Permis et pièce d’identité',
    GAUGE_SHEET: 'Fiche de jaugeage',
  },

  // --- Maintenance --------------------------------------------------------------------
  workStatus: {
    draft: 'Brouillon',
    planned: 'Planifié',
    in_progress: 'En cours',
    done: 'Clôturé',
    cancelled: 'Annulé',
  },
  workType: {
    preventive: 'Préventif',
    corrective: 'Correctif',
    inspection: 'Inspection',
    revision: 'Révision',
  },
  severity: {
    minor: 'Mineure',
    major: 'Majeure',
    critical: 'Critique',
  },
  priority: {
    low: 'Basse',
    normal: 'Normale',
    high: 'Haute',
    critical: 'Critique',
  },
  breakdownStatus: {
    open: 'Ouverte',
    acknowledged: 'Prise en compte',
    converted: 'Ordre créé',
    closed: 'Clôturée',
  },
  immobilisationStatus: {
    active: 'En cours',
    released: 'Levée',
  },
  reasonCode: {
    work_order: 'Ordre de travail',
    breakdown: 'Panne',
    document_expired: 'Document expiré',
    manual: 'Décision de l’atelier',
  },
  readingType: {
    km: 'Kilomètres',
    hours: 'Heures moteur',
  },
  planTrigger: {
    mileage: 'Au kilométrage',
    date: 'Au calendrier',
    both: 'Les deux',
  },

  // --- Location de camions -------------------------------------------------------------------
  pricingMode: {
    period: 'À la période',
    rotation: 'À la rotation',
    liter: 'Au litre',
  },
  periodUnit: {
    day: 'Jour',
    week: 'Semaine',
    month: 'Mois',
  },
  fuelResponsibility: {
    lessor: 'À la charge du loueur',
    lessee: 'À la charge du locataire',
  },
  driverBilling: {
    included: 'Compris dans le loyer',
    separate_line: 'Facturé sur une ligne à part',
    not_billed: 'Mis à disposition sans facturation',
  },
  rentalStatus: {
    draft: 'Brouillon',
    active: 'En cours',
    suspended: 'Suspendu',
    returned: 'Restitué',
    closed: 'Clos',
    cancelled: 'Annulé',
  },
  billingCycle: {
    monthly: 'Chaque mois',
    on_return: 'À la restitution',
    per_rotation: 'À chaque rotation',
  },
  depositStatus: {
    none: 'Aucune caution',
    held: 'Caution détenue',
    partially_refunded: 'Caution remboursée en partie',
    refunded: 'Caution remboursée',
    forfeited: 'Caution retenue',
  },
  periodStatus: {
    open: 'Ouverte',
    closed: 'Close',
    invoiced: 'Facturée',
  },
  rotationStatus: {
    draft: 'Brouillon',
    validated: 'Validée',
    invoiced: 'Facturée',
  },
  inspectionKind: {
    checkout: 'Départ du camion',
    checkin: 'Retour du camion',
  },
  chargeType: {
    damage: 'Dégât',
    missing_fuel: 'Carburant manquant',
    late_return: 'Retard de restitution',
    fine: 'Amende',
    cleaning: 'Nettoyage',
    other: 'Autre',
  },
  chargeStatus: {
    pending: 'À facturer',
    invoiced: 'Facturée',
    waived: 'Abandonnée',
  },
  rentalLine: {
    rental_period: 'Loyer',
    rental_rotation: 'Rotation',
    rental_liter: 'Transport au litre',
    rental_fuel: 'Carburant refacturé',
    rental_driver: 'Chauffeur',
    rental_charge: 'Charge',
  },

  // --- Lieux, GPS ---------------------------------------------------------------------------------
  locationType: {
    city: 'Ville',
    capital: 'Capitale',
    region_center: 'Centre régional',
    border_post: 'Poste frontière',
    checkpoint: 'Poste de contrôle',
    fuel_depot: 'Dépôt carburant',
    station: 'Station',
    port: 'Port',
    customs_post: 'Poste de douane',
    transit_point: 'Point de transit',
    delivery_site: 'Site de livraison',
    risk_zone: 'Zone à risque',
    custom: 'Personnalisé',
  },
  gpsProvider: {
    generic_webhook: 'Boîtier GPS (envoi direct)',
    traccar_bridge: 'Pont Traccar',
    vendor_polling: 'Interrogation Traccar',
    traccar: 'Traccar',
  },
  // D'où vient une position (`source` d'un point GPS). Toutes les portes d'un
  // boîtier — webhook, Traccar, TCP direct — se disent « Boîtier GPS ».
  gpsSource: {
    generic_webhook: 'Boîtier GPS',
    traccar: 'Boîtier GPS',
    hardware: 'Boîtier GPS',
    vendor_polling: 'Fournisseur GPS',
    driver_app: 'Téléphone du chauffeur',
    whatsapp: 'WhatsApp du chauffeur',
    office: 'Signalée par le bureau',
  },
  gpsAuthMode: {
    shared_secret: 'Secret partagé',
    api_key: 'Clé d’accès',
    none: 'Aucune',
  },

  // --- Alertes ---------------------------------------------------------------------------------------
  alerteSeverity: {
    info: 'Information',
    warning: 'À surveiller',
    error: 'Grave',
    critical: 'Critique',
    major: 'Majeure',
    minor: 'Mineure',
  },
  alerteStatut: {
    open: 'À traiter',
    in_progress: 'En cours',
    acknowledged: 'Prise en compte',
    handled: 'Traitée',
    resolved: 'Résolue',
    closed: 'Clôturée',
    dismissed: 'Écartée',
    escalated: 'Remontée',
    failed: 'En échec',
    dead: 'En échec',
  },
  alerteCategorie: {
    GPS: 'Suivi GPS',
    Geofence: 'Zones',
    Workflow: 'Étapes du trajet',
    Finance: 'Argent',
    Documents: 'Documents',
    Stock: 'Stock',
    Plateforme: 'Plateforme',
  },
  alerteDecision: {
    call_driver: 'Appeler le chauffeur',
    declare_incident: 'Déclarer un incident',
    ignore: 'Ignorer',
    resolve: 'Résoudre',
    assign_finance_review: 'Confier à la finance',
  },
  alerteEvent: {
    trip_status_proposed: 'Étape à confirmer',
    tracking_conflict: 'Conflit GPS',
    entered: 'Entrée en zone',
    exited: 'Sortie de zone',
    dwell: 'Arrêt prolongé',
    STATUS_STAY: 'Séjour au poste (statut du voyage)',
    geo_location_entered: 'Entrée en zone',
    geo_location_exited: 'Sortie de zone',
    deadline_digest: 'Récapitulatif des échéances',
    gps_silent: 'Camion sans signal',
    gps_never_emitted: 'Camion jamais vu',
    VEHICLE_DOCUMENT_EXPIRY: 'Papier du camion à renouveler',
    MAINTENANCE_PLAN_DUE: 'Entretien à faire',
    CRITICAL_STOP: 'Arrêt critique',
    ROUTE_DEVIATION: 'Camion hors itinéraire',
    DOCUMENT_MISSING_AT_BORDER: 'Pièce manquante à la frontière',
    FUEL_DROP_DROP: 'Baisse de carburant suspecte',
    GPS_OFFLINE: 'Boîtier GPS sans signal',
    TRUCK_IMMOBILIZED: 'Camion immobilisé',
    INBOUND_RENTAL_ENDING: 'Location bientôt terminée',
    CONVOY_RETURN_OVERDUE: 'Retour de convoi en retard',
    reconciliation_blocking_variance: 'Écart de volume bloquant',
    VOLUME_RECONCILIATION_BLOCKED: 'Écart de volume à trancher',
    STATION_TANK_VARIANCE: 'Écart de cuve',
    STATION_RECEPTION_TO_CHECK: 'Réception de camion à vérifier',
    STATION_SUPPLY_TO_CHECK: 'Voyage vers une station à vérifier',
    STATION_TANK_LOW: 'Cuve sous le seuil',
    STATION_TANK_AUTONOMY: 'Moins de 2 jours de vente en cuve',
    workflow_blocked: 'Étape bloquée',
    workflow_truck_overbooked: 'Camion surchargé',
    rental_invoice_volume_dispute: 'Litige de volume (location)',
    mission_integrity: 'Anomalie de mission',
    document_ocr_failed: 'Lecture du document impossible',
    low_stock: 'Stock bas',
    openwa_session_down: 'WhatsApp coupé',
    openwa_webhook_mismatch: 'Adresse de réception WhatsApp incorrecte',
    openwa_acks_silent: 'Accusés de réception WhatsApp absents',
    openwa_queue_stalled: 'File d’envoi WhatsApp bloquée',
    openwa_canary_failed: 'Message témoin WhatsApp non reçu',
    traccar_unreachable: 'Serveur GPS injoignable',
    traccar_forward_silent: 'Serveur GPS : positions non transmises',
    traccar_no_device: 'Aucun boîtier GPS ne communique',
    // Nature d'un message de la file d'envoi WhatsApp (écran d'intégration).
    canary: 'Message témoin',
    delivery_note: 'Bon de livraison',
    invoice: 'Facture',
    printed_list: 'Liste imprimée',
    client_trip_step: 'Message au client',
  },
  anomalieMission: {
    gps_conflict: 'Conflit GPS',
    missing_pod: 'Preuve de livraison manquante',
    volume_variance: 'Écart de volume',
    triangular_consumption_variance: 'Écart de consommation',
  },
  // Résultat du contrôle d'une livraison (note de fiabilité d'un trajet).
  statutIntegrite: {
    OK: 'Conforme',
    FAILED_RECONCILIATION: 'Écart de volume à trancher',
    POD_REQUIRED: 'Preuve de livraison à fournir',
    BILLING_READY: 'Prêt à facturer',
    BLOCKED_AUDIT: 'Bloqué pour contrôle',
  },
  // Journal d'audit (chaîne de contrôle): ce qui a été enregistré, et sur quoi.
  journalEvenement: {
    'mission.integrity_computed': 'Livraison contrôlée',
    'mission.integrity_justified': 'Écart justifié',
    'pod.validated': 'Preuve de livraison validée',
    'pod.review_required': 'Preuve de livraison à revoir',
    'arbitration.certificate_generated': 'Certificat d’arbitrage généré',
    'ecarbu.score_computed': 'Score E-Carbu calculé',
    'invoice.dispatched': 'Facture envoyée',
    'domain_event.critical_emitted': 'Alerte grave enregistrée',
  },
  // Garanties affichées sous une action du tableau de pilotage.
  pilotageContrat: {
    effet_unique_par_reference_metier: 'Un seul effet par opération',
    audit_obligatoire: 'Trace conservée',
    finance: 'Argent',
    audit: 'Journal de contrôle',
    workflow: 'Étapes du voyage',
    stock: 'Stock',
    rapport_station: 'Rapport de station',
    rapport_journalier: 'Rapport du jour',
    rapport_consommation: 'Rapport de consommation',
    rapport_pdf: 'Rapport PDF',
    paiement_client: 'Paiement du client',
    mouvement_stock_unique: 'Un seul mouvement de stock',
  },
  journalEntite: {
    trip: 'Trajet',
    organization: 'Société',
    invoice: 'Facture',
    document: 'Document',
    truck: 'Camion',
    order: 'Commande',
  },

  // --- Finance --------------------------------------------------------------------------------------------
  financeKind: {
    CUSTOMER_INVOICE: 'Facture client',
    CUSTOMER_ADJUSTMENT: 'Correction de facture',
    CUSTOMER_RECEIPT: 'Encaissement client',
    CUSTOMER_CREDIT: 'Avoir client',
    CUSTOMER_REFUND: 'Remboursement client',
    SUPPLIER_BILL: 'Facture fournisseur',
    SUPPLIER_PAYMENT: 'Règlement fournisseur',
    SUPPLIER_CREDIT: 'Avoir fournisseur',
    SUPPLIER_REFUND: 'Remboursement fournisseur',
    DRIVER_ADVANCE: 'Avance chauffeur',
    DRIVER_EXPENSE: 'Frais justifiés par le chauffeur',
    DRIVER_RETURN: 'Reliquat rendu',
    DRIVER_REIMBURSEMENT: 'Remboursement au chauffeur',
    EXPENSE: 'Dépense de trésorerie',
    INCOME: 'Entrée de trésorerie',
    TRANSFER: 'Transfert entre comptes',
    REVERSAL: 'Annulation liée',
    CASH_ASSIGNMENT: 'Rapprochement de trésorerie',
    HISTORICAL_BALANCE: 'Solde historique à rapprocher',
    HISTORY_RECONCILIATION: 'Régularisation historique',
    CLAIM: 'Créance sur manquant',
  },
  financeStatut: {
    CONFIRMED: 'Confirmé',
    PENDING: 'En attente',
    REVERSED: 'Annulé',
  },
  // Compte partenaire (domaines purs du front: absents de la fixture des enums du backend).
  type_operation_compte: {
    ACOMPTE_LOT: 'Acompte sur lot',
    AVANCE_LOYER: 'Avance sur fret',
    PAIEMENT: 'Paiement',
    DECOMPTE_LOT: 'Décompte de lot',
    ETAT_PAIEMENT: 'État de paiement',
    LOYER_MOIS: 'Fret du mois',
    FACTURE_RECUE: 'Facture reçue',
    AVOIR: 'Avoir',
    REMBOURSEMENT: 'Remboursement reçu',
    ANNULATION: 'Annulation',
  },
  // D'où vient une ligne du compte: un lot et une location sont deux sortes de contrat à part.
  origine_compte: {
    LOT_SETTLEMENT: 'Lot',
    LOT_SETTLEMENT_PAYMENT: 'Lot',
    LOT_ADVANCE: 'Lot',
    TRANSPORT_STATEMENT: 'Location',
    RENTAL: 'Location',
    RENTAL_ADVANCE: 'Location',
    MANUAL: 'Saisie directe',
  },
  situation_compte: {
    DOIT: 'Doit à PKS',
    DU: 'PKS lui doit',
    SOLDE: 'Soldé',
  },
  compteTresorerie: {
    CASH: 'Caisse',
    BANK: 'Banque',
    MOBILE_MONEY: 'Mobile money',
  },
  tiers: {
    CUSTOMER: 'Clients',
    SUPPLIER: 'Fournisseurs',
    DRIVER: 'Chauffeurs',
  },
  typeTiers: {
    CUSTOMER: 'Client',
    SUPPLIER: 'Fournisseur',
    DRIVER: 'Chauffeur',
  },
  varianceStatut: {
    incomplete: 'Mesure incomplète',
    ok: 'Dans la tolérance',
    warning: 'À surveiller',
    blocked: 'Hors tolérance',
    pending_approval: 'En attente de décision',
    approved: 'Approuvé',
    rejected: 'Refusé',
    closed: 'Clos',
  },

  // --- Préparation du transport ---------------------------------------------------------------------------------
  readinessControl: {
    order: 'Commande liée',
    workflow: 'Prochaine action',
    documents: 'Pièces',
    volume: 'Volume',
    reconciliation: 'Écart de volume',
    stock: 'Stock',
    invoice: 'Facturation',
    gps: 'Dernier point GPS',
  },
  readinessEtat: {
    ready: 'Prêt',
    warning: 'À surveiller',
    watch: 'Surveillance',
    blocked: 'Bloqué',
    off: 'Désactivé',
  },
  readinessSeverity: {
    blocked: 'Informatif (ancien réglage)',
    watch: 'Surveillance',
    off: 'Désactivé',
  },

  // --- Contrôle carburant ----------------------------------------------------------------------------------------
  fuelRequestStatus: {
    draft: 'Brouillon',
    pending: 'En attente',
    approved: 'Approuvée',
    rejected: 'Refusée',
    active: 'Actif',
    inactive: 'Inactif',
  },
  engineType: {
    truck: 'Camion interne',
    machine: 'Engin',
    generator: 'Groupe électrogène',
    vehicle: 'Véhicule',
  },
  quotaScope: {
    site: 'Site',
    asset: 'Engin',
  },
  quotaPeriod: {
    daily: 'Journalier',
    weekly: 'Hebdomadaire',
    monthly: 'Mensuel',
  },

  // --- Terrain (application chauffeur) -------------------------------------------------------------------------------
  motifBlocage: {
    CONTROL: 'Contrôle en cours',
    MISSING_DOCUMENT: 'Pièce manquante',
    PAYMENT: 'Paiement ou taxe',
    QUEUE: 'Attente au poste',
    ESCORT: 'Attente d’escorte',
    OTHER: 'Autre motif',
  },
  checklist: {
    TIRES: 'Pneus',
    BRAKES: 'Freins',
    LIGHTS: 'Feux',
    EXTINGUISHER: 'Extincteur',
    ADR_KIT: 'Kit ADR',
    PAPERS: 'Papiers',
    SEALS: 'Plombs et scellés',
    VALVES: 'Vannes',
  },
  incidentType: {
    ACCIDENT: 'Accident',
    BREAKDOWN: 'Panne',
    SECURITY: 'Sécurité',
    MEDICAL: 'Urgence médicale',
    LEAK: 'Fuite de produit',
    OTHER: 'Autre',
  },
  gaugePhase: {
    LOADING: 'Chargement',
    DELIVERY: 'Livraison',
  },
  borderEventType: {
    BLOCKAGE: 'Blocage',
    DEPARTURE: 'Départ',
  },
  relaisMaintenance: {
    relayed: 'Relayée en maintenance',
    skipped_module_off: 'Maintenance non activée',
    skipped_no_truck: 'Aucun camion connu',
    relay_failed: 'Relais vers la maintenance en échec',
  },
};

/**
 * Domaines dont un autre nom est encore utilisé dans le code ou dans la
 * documentation du programme. Un seul tableau de libellés par notion: le nom
 * ancien pointe vers le nom principal.
 */
const ALIAS_DOMAINES = {
  orderStatus: 'statut_commande',
  severiteAlerte: 'alerteSeverity',
  location: 'locationType',
  contratLocation: 'rentalStatus',
  reason_code: 'reasonCode',
  reading: 'readingType',
  trigger: 'planTrigger',
  breakdown: 'breakdownStatus',
  immobilisation: 'immobilisationStatus',
  docType: 'docVehicule',
  tripStep: 'tripStatus',
  statutAlerte: 'alerteStatut',
  alerteType: 'alerteEvent',
  eventType: 'alerteEvent',
  controleCarburant: 'fuelRequestStatus',
  roleUtilisateur: 'userRole',
  etat_camion: 'truckStatus',
  // Les quatre étapes qu'un partenaire annonce à la confirmation d'un camion de lot : ce sont des étapes de trajet.
  lot_etape_confirmation: 'tripStatus',
};

/** Domaine réel (après alias). */
const domaineReel = (domaine) => (LIBELLES[domaine] ? domaine : ALIAS_DOMAINES[domaine] || domaine);

/** Mots utilisés quand un code n'a pas de libellé: de quoi lire un code inconnu. */
const MOTS = {
  customs: 'douane',
  customs_exit: 'douane de sortie',
  route: 'route',
  delivery: 'livraison',
  delivered: 'livré',
  status: 'état',
  state: 'état',
  pending: 'en attente',
  cleared: 'validé',
  loading: 'chargement',
  loaded: 'chargé',
  unloaded: 'déchargé',
  entry: 'entrée',
  exit: 'sortie',
  border: 'frontière',
  depot: 'dépôt',
  arrived: 'arrivé',
  completed: 'terminé',
  complete: 'terminé',
  cancelled: 'annulé',
  canceled: 'annulé',
  created: 'créé',
  draft: 'brouillon',
  active: 'actif',
  inactive: 'inactif',
  suspended: 'suspendu',
  expired: 'expiré',
  revoked: 'révoqué',
  open: 'ouvert',
  opened: 'ouvert',
  closed: 'clôturé',
  close: 'clôture',
  paid: 'payé',
  partial: 'partiel',
  partially: 'partiellement',
  overdue: 'en retard',
  failed: 'en échec',
  error: 'erreur',
  warning: 'à surveiller',
  info: 'information',
  critical: 'critique',
  blocked: 'bloqué',
  ready: 'prêt',
  invoice: 'facture',
  invoiced: 'facturé',
  order: 'commande',
  trip: 'trajet',
  truck: 'camion',
  driver: 'chauffeur',
  client: 'client',
  customer: 'client',
  supplier: 'fournisseur',
  station: 'station',
  stock: 'stock',
  fuel: 'carburant',
  payment: 'paiement',
  document: 'document',
  documents: 'documents',
  volume: 'volume',
  transfer: 'transfert',
  internal: 'interne',
  external: 'externe',
  sale: 'vente',
  rental: 'location',
  maintenance: 'maintenance',
  adjustment: 'ajustement',
  loss: 'perte',
  return: 'retour',
  returned: 'rendu',
  review: 'vérification',
  required: 'requis',
  audit: 'contrôle',
  unknown: 'inconnu',
  other: 'autre',
  manual: 'manuel',
  automatic: 'automatique',
  waiting: 'en attente',
  running: 'en cours',
  progress: 'en cours',
  in_progress: 'en cours',
  transit: 'transit',
  a: '',
  an: '',
  the: '',
  of: 'de',
  to: 'vers',
  at: 'à',
  in: 'en',
  en: '',
  and: 'et',
  de: 'de',
  la: 'la',
  le: 'le',
  du: 'du',
};

const MOTS_IGNORES = new Set(['a', 'an', 'the', 'en']);

const majusculeInitiale = (texte) => (texte ? texte.charAt(0).toUpperCase() + texte.slice(1) : texte);

/**
 * Lecture française d'un code que la table ne connaît pas, à partir des mots
 * connus. `null` si un seul mot manque: mieux vaut un libellé par défaut qu'une
 * phrase à moitié traduite.
 */
export const humaniser = (code) => {
  if (code === null || code === undefined) return null;
  const jetons = String(code)
    .trim()
    .split(/[\s_\-./]+/)
    .filter(Boolean)
    .map((j) => j.toLowerCase());
  if (!jetons.length) return null;
  const mots = [];
  for (const jeton of jetons) {
    if (!(jeton in MOTS)) return null;
    if (MOTS_IGNORES.has(jeton) && jetons.length > 1) continue;
    const mot = MOTS[jeton];
    if (mot) mots.push(mot);
  }
  if (!mots.length) return null;
  return majusculeInitiale(mots.join(' '));
};

/** Libellé par défaut d'un domaine quand même `humaniser` n'y arrive pas. */
const DEFAUT_DOMAINE = {
  categorie_frais: 'Autres frais',
  type_frais_route: 'Autre frais',
  documentType: 'Autre document',
  docVehicule: 'Autre document',
  stockMovement: 'Autre mouvement',
  financeKind: 'Autre opération',
  journalEvenement: 'Opération enregistrée',
  journalEntite: 'Élément',
  anomalieMission: 'Anomalie de livraison',
  incidentType: 'Autre',
  chargeType: 'Autre',
  motifBlocage: 'Autre motif',
  alerteEvent: 'Alerte',
  auditEntity: 'Élément',
  auditAction: 'Action enregistrée',
  userRole: 'Rôle à préciser',
  tripStatus: 'Étape à préciser',
  statut_commande: 'État à préciser',
  shortageCase: 'À examiner',
  shortageEvent: 'Événement',
  orderEvent: 'Événement',
  invoiceStatus: 'État à préciser',
  truckStatus: 'État à préciser',
};

const dejaAvertis = new Set();
const avertir = (domaine, code) => {
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production') return;
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'test') return;
  const cle = `${domaine}:${code}`;
  if (dejaAvertis.has(cle)) return;
  dejaAvertis.add(cle);
  // eslint-disable-next-line no-console
  console.warn(`[libelles] aucun libellé pour « ${code} » dans le domaine « ${domaine} »`);
};

const normaliser = (code) => String(code).trim().toLowerCase();

// Index insensible à la casse, construit à la demande et mis en cache par
// table: les domaines ajoutés dans `LIBELLES` n'ont rien à déclarer ailleurs.
const INDEX = new WeakMap();
const indexDe = (domaine) => {
  const table = LIBELLES[domaineReel(domaine)];
  if (!table) return null;
  let index = INDEX.get(table);
  if (!index) {
    index = new Map();
    for (const [cle, valeur] of Object.entries(table)) index.set(normaliser(cle), valeur);
    INDEX.set(table, index);
  }
  return index;
};

/** Vrai si le domaine existe dans la table (alias compris). */
export const domaineConnu = (domaine) => Boolean(LIBELLES[domaineReel(domaine)]);

const ACCENTS = /[àâäçéèêëîïôöùûüœÀÂÇÉÈÊËÎÏÔÙÛÜŒ’']/;

/** Une phrase déjà écrite en français (plusieurs mots, ou accentuée) est rendue telle quelle. */
const ressembleAUnTexte = (texte) => /\s/.test(texte) || ACCENTS.test(texte);

/**
 * Libellé français d'un code.
 *
 * - code vide (null, undefined, '') → `repli` s'il est donné, sinon « — »;
 * - code connu du domaine (casse ignorée) → son libellé;
 * - date ISO → date française;
 * - phrase déjà française → telle quelle;
 * - identifiant (UUID…) → `repli` ou libellé par défaut du domaine;
 * - code inconnu → lecture par mots (`humaniser`), sinon `repli`, sinon
 *   libellé par défaut du domaine, sinon « Non précisé ».
 *
 * Ne renvoie JAMAIS le code lui-même.
 */
export const libelle = (domaine, code, repli) => {
  if (code === null || code === undefined || code === '') return repli !== undefined ? repli : TIRET;
  if (typeof code === 'boolean') return code ? 'Oui' : 'Non';
  const index = indexDe(domaine);
  const trouve = index?.get(normaliser(code));
  if (trouve) return trouve;

  const texte = String(code).trim();
  if (!texte || texte === 'null' || texte === 'undefined') return repli !== undefined ? repli : TIRET;
  if (estDateIso(texte)) return formatDate(texte);

  const parDefaut = repli !== undefined ? repli : DEFAUT_DOMAINE[domaineReel(domaine)] || NON_PRECISE;
  if (estIdentifiant(texte)) return parDefaut;
  if (ressembleAUnTexte(texte) && !/_/.test(texte)) return francisaTexte(texte);

  const lu = humaniser(texte);
  if (lu) return lu;
  avertir(domaine, texte);
  return parDefaut;
};

/** Options `{ value, label }` d'un domaine, pour un sélecteur. `codes` limite et ordonne. */
export const optionsLibelles = (domaine, codes) => {
  const table = LIBELLES[domaineReel(domaine)] || {};
  const liste = codes || Object.keys(table);
  return liste.map((code) => ({ value: code, label: libelle(domaine, code) }));
};

// --- Couleur de pastille -------------------------------------------------------------

/**
 * Teinte d'une pastille: 'success' | 'info' | 'warning' | 'destructive' | 'muted'
 * (variantes de `components/ui/badge.jsx`). Par domaine d'abord, puis par le
 * sens du mot si le domaine n'a rien prévu.
 */
const TONS = {
  tripStatus: {
    CREATED: 'muted',
    EN_ROUTE_TO_BORDER: 'info',
    AT_CUSTOMS_EXIT: 'warning',
    CLEARED_CUSTOMS_EXIT: 'info',
    EN_ROUTE_TO_LOADING: 'info',
    LOADING: 'info',
    EN_ROUTE_TO_ENTRY_CUSTOMS: 'info',
    AT_CUSTOMS_ENTRY: 'warning',
    CLEARED_CUSTOMS_ENTRY: 'info',
    ARRIVED_AT_DEPOT: 'success',
    EN_ROUTE_TO_DELIVERY: 'info',
    DELIVERED: 'success',
    COMPLETED: 'success',
    RETURNING: 'success',
    CANCELLED: 'destructive',
    PARTIALLY_DELIVERED: 'warning',
    POD_REQUIRED: 'warning',
    POD_REVIEW: 'warning',
    BILLING_READY: 'success',
    FAILED_RECONCILIATION: 'destructive',
    BLOCKED_AUDIT: 'destructive',
  },
  truckStatus: {
    FREE: 'success',
    ON_TRIP: 'info',
    MAINTENANCE: 'warning',
    OUT_OF_SERVICE: 'destructive',
    RENTED_OUT: 'muted',
    IMMOBILIZED: 'destructive',
    RETURNED_TO_OWNER: 'muted',
  },
  technicalStatus: { OK: 'success', MAINTENANCE: 'warning', BROKEN: 'destructive' },
  statut_commande: {
    DRAFT: 'muted',
    PENDING: 'muted',
    CONFIRMED: 'info',
    ALLOCATED: 'info',
    IN_TRANSIT: 'info',
    PARTIALLY_DELIVERED: 'warning',
    DELIVERED: 'success',
    INVOICED: 'info',
    PAID: 'success',
    CANCELLED: 'destructive',
  },
  etat_commande: {
    BROUILLON: 'muted',
    A_AFFECTER: 'warning',
    PARTIELLE: 'warning',
    AFFECTEE: 'info',
    EN_ROUTE: 'info',
    LIVREE: 'success',
    ANNULEE: 'destructive',
  },
  invoiceStatus: {
    DRAFT: 'muted',
    PENDING: 'warning',
    SENT: 'info',
    PARTIAL: 'warning',
    PARTIALLY_PAID: 'warning',
    PAID: 'success',
    OVERDUE: 'warning',
    DISPUTED: 'destructive',
    CANCELLED: 'muted',
  },
  clientStatus: { ACTIVE: 'success', SUSPENDED: 'warning', INACTIVE: 'muted' },
  licenseStatus: { ACTIVE: 'success', SUSPENDED: 'warning', EXPIRED: 'destructive', REVOKED: 'destructive' },
  stationStatus: { ACTIVE: 'success', MAINTENANCE: 'warning', INACTIVE: 'muted' },
  shiftStatus: { OPEN: 'info', CLOSED: 'muted' },
  station_poste: { OPEN: 'info', CLOSED: 'muted' },
  station_journee: { OPEN: 'info', LOCKED: 'success', VALIDATED: 'success' },
  station_prix_programme: { SCHEDULED: 'info', APPLIED: 'success', CANCELLED: 'muted', FAILED: 'destructive' },
  station_prix_resultat: { APPLIED: 'success', REFUSED: 'destructive', ERROR: 'destructive' },
  station_pompe_evenement: { CALIBRATION: 'info', SEAL: 'muted', INSPECTION: 'info', BREAKDOWN: 'destructive' },
  station_echeance: { OVERDUE: 'destructive', SOON: 'warning', OK: 'success', NONE: 'muted' },
  station_releve_gerant: { FROZEN: 'success', VOID: 'muted' },
  station_reprise: { created: 'success', skipped: 'muted', error: 'destructive' },
  import_flotte_travail: {
    queued: 'info', analyzing: 'info', draft: 'warning', confirmed: 'info', importing: 'info',
    done: 'success', failed: 'destructive', cancelled: 'muted',
  },
  import_flotte_ligne: { ready: 'success', review: 'warning', refused: 'destructive', in_lot: 'muted' },
  station_reception: { EXPECTED: 'muted', IN_PROGRESS: 'warning', VALIDATED: 'success', CANCELLED: 'muted' },
  station_bon: { ISSUED: 'info', USED: 'success', CANCELLED: 'muted', EXPIRED: 'warning' },
  station_rapprochement: { MATCHED: 'success', UNMATCHED: 'warning', DUPLICATE: 'muted' },
  exportJob: { PENDING: 'muted', RUNNING: 'info', COMPLETED: 'success', FAILED: 'destructive' },
  workStatus: { draft: 'muted', planned: 'info', in_progress: 'warning', done: 'success', cancelled: 'muted' },
  breakdownStatus: { open: 'destructive', acknowledged: 'warning', converted: 'info', closed: 'success' },
  immobilisationStatus: { active: 'destructive', released: 'success' },
  severity: { minor: 'muted', major: 'warning', critical: 'destructive' },
  priority: { low: 'muted', normal: 'info', high: 'warning', critical: 'destructive' },
  alerteSeverity: {
    info: 'info',
    warning: 'warning',
    error: 'destructive',
    critical: 'destructive',
    major: 'warning',
    minor: 'muted',
  },
  alerteStatut: {
    open: 'destructive',
    in_progress: 'warning',
    acknowledged: 'warning',
    handled: 'success',
    resolved: 'success',
    closed: 'muted',
    dismissed: 'muted',
    escalated: 'warning',
    failed: 'destructive',
    dead: 'destructive',
  },
  statut_contrat_entrant: { BROUILLON: 'muted', ACTIF: 'success', CLOS: 'muted' },
  statut_reglement: { APERCU: 'muted', BROUILLON: 'warning', VALIDE: 'success' },
  statut_etat_paiement: { BROUILLON: 'warning', VALIDE: 'success', ANNULE: 'muted' },
  statut_paiement_etat: { PAYE: 'success', PARTIEL: 'info', A_PAYER: 'warning' },
  statut_decompte_lot: { BROUILLON: 'warning', VALIDE: 'success', ANNULE: 'muted' },
  source_charge_proprietaire: { work_order: 'muted', vehicle_document: 'muted', journal: 'muted' },
  rentalStatus: {
    draft: 'muted',
    active: 'success',
    suspended: 'warning',
    returned: 'muted',
    closed: 'muted',
    cancelled: 'muted',
  },
  chargeStatus: { pending: 'warning', invoiced: 'success', waived: 'muted' },
  rotationStatus: { draft: 'muted', validated: 'info', invoiced: 'success' },
  periodStatus: { open: 'info', closed: 'muted', invoiced: 'success' },
  docExpiry: { valid: 'success', expiring: 'warning', expired: 'destructive', unknown: 'muted' },
  financeStatut: { CONFIRMED: 'success', PENDING: 'warning', REVERSED: 'muted' },
  situation_compte: { DOIT: 'warning', DU: 'info', SOLDE: 'success' },
  varianceStatut: {
    incomplete: 'muted',
    ok: 'success',
    warning: 'warning',
    blocked: 'destructive',
    pending_approval: 'warning',
    approved: 'success',
    rejected: 'destructive',
    closed: 'muted',
  },
  readinessEtat: { ready: 'success', warning: 'warning', watch: 'warning', blocked: 'destructive', off: 'muted' },
  whatsappStatut: { pending: 'muted', processing: 'info', sent: 'info', accepted: 'success', delivered: 'success', read: 'success', failed: 'destructive', dead: 'destructive' },
  surveillanceWhatsapp: {
    session_connectee: 'success',
    session_coupee: 'warning',
    session_inconnue: 'muted',
    adresse_ok: 'success',
    adresse_corrigee: 'info',
    adresse_incorrecte: 'warning',
    adresse_inconnue: 'muted',
    temoin_desactive: 'muted',
    temoin_recu: 'success',
    temoin_manque: 'warning',
    temoin_attendu: 'muted',
  },
  syncStatut: { pending: 'warning', synced: 'success', failed: 'destructive' },
  fuelRequestStatus: {
    draft: 'muted',
    pending: 'warning',
    approved: 'success',
    rejected: 'destructive',
    active: 'success',
    inactive: 'muted',
  },
  workflowInstance: { active: 'info', completed: 'success', cancelled: 'muted' },
  portail_etat: { actif: 'success', expire: 'muted', inactif: 'warning' },
  lot_statut: { OUVERT: 'info', CLOS: 'muted' },
  lot_ligne_etat: { ATTRIBUE: 'muted', CONFIRME: 'success' },
  lot_etape: {
    ATTRIBUE: 'muted',
    CHARGEMENT: 'info',
    EN_ROUTE: 'info',
    DOUANE_MALI: 'warning',
    PARKING: 'warning',
    SORTI_DOUANE: 'info',
    EN_LIVRAISON: 'info',
    DEPOTE: 'success',
    ANNULE: 'destructive',
  },
  lot_retour: { A_RENDRE: 'warning', EN_RETOUR: 'info', RENDU: 'success' },
};

// Sens des mots, quand le domaine n'a pas prévu le code.
const TONS_GENERIQUES = [
  [/cancel|annul|reject|refus|fail|echec|échec|\bdead\b|error|erreur|overdue|retard|dispute|litige|blocked|bloqu|broken|panne|immobil|\bexpired|revoke|critical|critique/i, 'destructive'],
  [/warn|surveill|suspend|partial|review|escalat|\blate\b|waiting|attente|a_affecter|expiring/i, 'warning'],
  [/\bpaid\b|\bpay[eé]|deliver|livr|complete|termin|\bdone\b|\bclosed\b|\bclos\b|\bvalid|approv|\bactive\b|\bactif|\bready\b|\bpr[eê]t\b|\bok\b|resolved|success/i, 'success'],
  [/transit|route|progress|cours|running|\bopen\b|\bsent\b|envoy|loading|\binfo\b/i, 'info'],
];

/** Teinte de pastille d'un code (jamais `undefined`). */
export const ton = (domaine, code) => {
  if (code === null || code === undefined || code === '') return 'muted';
  const reel = domaineReel(domaine);
  const table = TONS[reel];
  if (table) {
    const cle = Object.keys(table).find((k) => normaliser(k) === normaliser(code));
    if (cle) return table[cle];
  }
  const texte = String(code);
  for (const [motif, teinte] of TONS_GENERIQUES) if (motif.test(texte)) return teinte;
  return 'muted';
};

// --- Textes libres et références ---------------------------------------------------------------

const HEX_COURT = /^(?=.*[0-9])(?=.*[a-f])[0-9a-f]{8,}$/i;
const NOMBRE_LONG = /^\d{13,}$/;

/** Vrai si la valeur est un identifiant technique (UUID, début d'UUID, nombre interminable). */
export const estIdentifiant = (valeur) => {
  if (valeur === null || valeur === undefined) return false;
  const texte = String(valeur).trim();
  return UUID.test(texte) || HEX_COURT.test(texte) || NOMBRE_LONG.test(texte);
};

/**
 * Texte libre venant d'ailleurs (message serveur, commentaire): rendu tel quel,
 * sauf s'il s'agit d'un identifiant ou d'un code brut. Une date ISO devient une
 * date française, « XOF » devient « FCFA ».
 */
// Ce qu'un champ manquant ou un calcul raté produit une fois converti en texte.
const TEXTE_CASSE = /^(?:null|undefined|nan|-?infinity|none|invalid date|\[object \w+\])$/i;

export const texteSur = (valeur, repli = TIRET) => {
  if (valeur === null || valeur === undefined) return repli;
  if (typeof valeur === 'number' && !Number.isFinite(valeur)) return repli;
  if (valeur instanceof Date) return formatDate(valeur, repli);
  // Un objet ou une liste rendrait « [object Object] » ou une suite collée.
  if (typeof valeur === 'object' || typeof valeur === 'function') return repli;
  const texte = String(valeur).trim();
  if (!texte || TEXTE_CASSE.test(texte)) return repli;
  if (estDateIso(texte)) return formatDate(texte);
  if (estIdentifiant(texte) || estCodeBrut(texte)) return repli;
  return francisaTexte(texte);
};

/** Titre d'une famille de la vue d'ensemble: jamais le titre interne « Cockpit … ». */
export const titreSectionPilotage = (section, repli = '') => {
  const cle = section && typeof section.key === 'string' ? section.key : null;
  if (cle && LIBELLES.sectionPilotage[cle]) return LIBELLES.sectionPilotage[cle];
  const titre = texteSur(section?.title, repli);
  return typeof titre === 'string' ? titre.replace(/^Cockpit\s+/i, '') : titre;
};

/** Nom d'un pays: un code à deux lettres (ML) devient « Mali », un nom en clair reste tel quel. */
export const nomPays = (valeur, repli = TIRET) => {
  if (valeur === null || valeur === undefined) return repli;
  const texte = String(valeur).trim();
  if (!texte || texte === 'null' || texte === 'undefined') return repli;
  if (/^[A-Za-z]{2}$/.test(texte)) return libelle('pays', texte.toUpperCase(), repli);
  return texteSur(texte, repli);
};

/** Référence lisible: un numéro métier, jamais le début d'un UUID. */
export const referenceLisible = (reference, repli = 'Sans référence') => {
  if (reference === null || reference === undefined) return repli;
  const texte = String(reference).trim();
  if (!texte || texte === 'null' || texte === 'undefined') return repli;
  // Un numéro métier (CMD-2026-0001, ORD-20261013-1234) est fait pour être lu
  // tel quel, même s'il ressemble à un code en majuscules: seul un identifiant
  // technique (UUID, début d'UUID) est écarté. Passer par `texteSur` remplaçait
  // toutes les vraies références par « Sans référence ».
  return estIdentifiant(texte) ? repli : texte;
};

/**
 * Nom d'un camion, d'un client… : le nom, sinon le numéro, sinon `repli`. Jamais
 * un identifiant.
 */
export const nomLisible = (...candidats) => {
  for (const c of candidats) {
    if (c === null || c === undefined) continue;
    const texte = String(c).trim();
    if (texte && !estIdentifiant(texte) && texte !== 'null' && texte !== 'undefined') return texte;
  }
  return 'Sans nom';
};

/** Oui / Non. */
export const ouiNon = (valeur) => (valeur ? 'Oui' : 'Non');

// --- Alias minces des anciennes tables ------------------------------------------------------

/**
 * Table `{ CODE: 'Libellé' }` pour les anciens exports (`TRIP_STATUS_LABELS`…).
 *
 * Les clés énumérées (`Object.keys`) restent celles de `codes` — un sélecteur
 * construit sur la table ne gagne pas de choix —, mais lire un code absent
 * (`TABLE[trip.status]`) ne renvoie plus `undefined` ni ne laisse le code brut
 * prendre le relais (`TABLE[x] || x`): la lecture passe par `libelle`, donc
 * donne toujours un texte français.
 */
export const tableAvecRepli = (domaine, codes) => {
  const base = {};
  (codes || Object.keys(LIBELLES[domaineReel(domaine)] || {})).forEach((code) => {
    base[code] = libelle(domaine, code);
  });
  return new Proxy(base, {
    get(cible, propriete) {
      if (typeof propriete !== 'string' || propriete in cible || propriete in Object.prototype) {
        return Reflect.get(cible, propriete);
      }
      if (!/^[A-Za-z][A-Za-z0-9_-]*$/.test(propriete)) return undefined;
      return libelle(domaine, propriete);
    },
  });
};
